import { useState } from "react";
import type { FormikHelpers } from "formik";
import BusinessForm from "../../../components/Forms/Registration/BusinessRegistrationForm";
import AdminForm from "../../../components/Forms/Registration/AdminRegistrationForm";
import Summary from "../../../components/Forms/Registration/RegistrationSummary";
import Slideshow from "../../../components/Slideshow";
import api from "../../../lib/axios";
import { toast } from "react-toastify";
import { registrationSlides } from "../../../assets/slideShowData";
import { logo } from "../../../assets/exports";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../hooks";
import { loginUser } from "../../../slices/authSlice";


const initialValues = {
  businessName: "",
  website: "",
  description: "",
  logo: "",
  firstname: "",
  lastname: "",
  othername: "",
  username: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone_number: "",
};

const RegistrationPage: React.FC = () => {
    const [step, setStep] = useState<number>(1);
    const [values, setValues] = useState(initialValues);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    // const businessFormRef = useRef<FormikProps<any>>(null);;
    // const adminFormRef = useRef<FormikProps<typeof initialValues>>(null);


    const goNext = (newValues: typeof initialValues) => {
        setValues((prev) => ({ ...prev, ...newValues }));
        setStep((s) => Math.min(3, s + 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goBack = (newValues?: typeof initialValues) => {
        if (newValues) setValues((prev) => ({ ...prev, ...newValues }));
        setStep((s) => Math.max(1, s - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const submitRegistration = async (
        finalValues: typeof initialValues,
        helpers?: FormikHelpers<typeof initialValues>
    ) => {
    setLoading(true);
    try {
        // Build payload exactly as requested (omit confirmPassword)
        const payload = {
            businessName: finalValues.businessName,
            website: finalValues.website,
            description: finalValues.description,
            logo: finalValues.logo,
            firstname: finalValues.firstname,
            lastname: finalValues.lastname,
            othername: finalValues.othername,
            username: finalValues.username,
            email: finalValues.email,
            password: finalValues.password,
            phone_number: finalValues.phone_number,
        };

        await api.post("/user/register", payload);

        // Auto-login with the same credentials so the user doesn't have to log in
        // manually. loginUser fetches the fully-shaped user object (with main_role
        // string name, nested Business, roles array) and stores the auth token.
        await dispatch(
            loginUser({ identifier: finalValues.email, password: finalValues.password })
        ).unwrap();

        toast.success("Account created! Welcome to StorePilot.");
        navigate("/onboarding");
    } catch (err: any) {
        console.error(err);
        const message =
        err?.response?.data?.message || err?.message || "Registration failed";
        toast.error(message);
        if (helpers) helpers.setSubmitting(false);
    } finally {
        setLoading(false);
    }
    };

    return (
        <div className="min-h-screen lg:flex-row lg:gap-4 lg:justify-between text-left flex items-center justify-center p-1">
            {/* LEFT - slideshow (desktop only) */}
            <div className="hidden lg:block lg:w-1/2">
                <Slideshow slides={registrationSlides} />
            </div>

            {/* RIGHT - form container */}
            <div className="flex w-full lg:w-1/2 items-center justify-center lg:p-4">
            <div className="w-full bg-white rounded-2xl md:shadow-md p-6 mx-auto sm:max-w-sm md:max-w-md lg:max-w-md">
                {/* Header (logo + text) */}
                <div className="flex flex-col items-center my-5 px-2">
                    <img
                        src={logo}
                        className="lg:w-50 lg:h-50 w-36 h-36 object-contain"
                        alt="StorePilot Logo"
                    />
                    <div className="text-center w-full">
                        <p className="form-title">
                            {step === 1 && (
                               "Enter your Business Details"
                            )}
                            {step === 2 && (
                                "Enter your personal Information"
                            )}
                            {step === 3 && (
                                "Confirm Details and Complete Registration"
                            )}
                        </p>
                    </div>
                </div>

                {/* Step content */}
                {step === 1 && (
                    <BusinessForm 
                        initialValues={values} 
                        onNext={goNext} 
                    />
                )}
                {step === 2 && (
                    <AdminForm 
                        initialValues={values} 
                        onBack={goBack} 
                        onNext={goNext} 
                    />
                )}
                {step === 3 && (
                <Summary
                    values={values}
                    onBack={() => goBack()}
                    onSubmit={() => submitRegistration(values)}
                    loading={loading}
                />
                )}

                {/* Floating bottom bar (wizard controls) */}
                <div
                    className="
                        fixed left-0 right-0 bottom-0 z-50 mx-auto max-w-4xl 
                        lg:relative lg:bottom-auto lg:mx-0 lg:w-full lg:flex
                        lg:pt-4"
                    style={{ pointerEvents: loading ? "none" : undefined }}
                >
                <div className="bg-primary/90 w-full backdrop-blur-sm p-3 rounded-t-lg flex items-center justify-center gap-4 shadow-lg mx-2 lg:mx-0">
                    <div className="text-sm text-white">
                        Step {step} of 3
                    </div>

                    {/* <div className="flex gap-3">
                        {step > 1 && (
                            <button
                            type="button"
                            onClick={() => goBack()}
                            className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                            disabled={loading}
                            >
                            Back
                            </button>
                        )}

                        {step < 3 && (
                            <button
                            type="button"
                            onClick={() => {
                                console.log("Step: ", step);
                                if (step === 1) businessFormRef.current?.submitForm();
                                if (step === 2) adminFormRef.current?.submitForm();
                                // window.scrollTo({ top: 0, behavior: "smooth" });
                                // toast.info("Please complete the form and press Next");
                            }}
                            className="px-4 py-2 rounded-md bg-primary text-white text-sm"
                            >
                            Next
                            </button>
                        )}

                        {step === 3 && (
                            <button
                            type="button"
                            onClick={() => submitRegistration(values)}
                            className="px-4 py-2 rounded-md bg-primary text-white text-sm"
                            disabled={loading}
                            >
                            {loading ? 
                                <div className="flex items-center gap-2">
                                    <span className="loader"></span>
                                    <span>Creating account...</span>
                                </div> 
                                    : 
                                "Confirm & Create"
                            }
                            </button>
                        )}
                    </div> */}
                </div>
                </div>
                <footer className="mt-6 text-left text-sm text-gray-500">
                    <p>
                        Already have an account?{" "}
                        <a href="/login" className="text-primary hover:underline">
                        Log in
                        </a>
                    </p>
                </footer>
            </div>
            </div>
        </div>
    );
    };

    export default RegistrationPage;